import { type FC, useCallback, useMemo, useState } from 'react'
import { Container, Spinner } from 'react-bootstrap'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { addToast } from '../../../../store/toast/toastSlice'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { type OAuth2App, useGetAppPiiShieldConfigQuery, useSetAppPiiShieldConfigMutation } from '../../../../store/auth/auth'
import { useZocksQuota } from '../../../../components/ScopedBlock/hooks'
import { Button } from '../../../../components/Button/Button'
import { PiiShieldCategoryCard } from './PiiShieldCategoryCard'
import { PiiShieldRequestAccess } from './PiiShieldRequestAccess'
import { ConfirmSaveBar } from '../Components/ConfirmSaveBar'
import { ALL_PII_ENTITIES, PII_SHIELD_CATEGORIES, type PiiEntity } from '../../../../common/piiShieldCategories'
import '../OAuth2AppsPermissionsTab.scss'
import './PiiShieldTab.scss'

type PiiEditState = {
  isEditing: boolean
  draftEntities: Set<PiiEntity>
  originalEntities: Set<PiiEntity>
  isConfirming: boolean
}

const INITIAL_EDIT_STATE: PiiEditState = {
  isEditing: false,
  draftEntities: new Set(),
  originalEntities: new Set(),
  isConfirming: false,
}

export interface PiiShieldTabProps {
  oAuth2App: OAuth2App
}

export const PiiShieldTab: FC<PiiShieldTabProps> = ({ oAuth2App }) => {
  const { name: appName, clientId } = oAuth2App
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()

  const { hasQuota, quotaSuccess } = useZocksQuota()
  const isActive = hasQuota('integration.oauth2AppPiiShield.value')

  const { data: piiShieldData, isLoading, isError } = useGetAppPiiShieldConfigQuery({ accountId, clientId }, { skip: !isActive })
  const [setPiiShieldConfig, { isLoading: isSaving }] = useSetAppPiiShieldConfigMutation()

  const [editState, setEditState] = useState<PiiEditState>(INITIAL_EDIT_STATE)
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  const { isEditing, draftEntities, originalEntities, isConfirming } = editState

  const savedEntities = useMemo(() => {
    const knownEntities = new Set<string>(ALL_PII_ENTITIES)
    return new Set((piiShieldData?.data.entities ?? []).filter((entity): entity is PiiEntity => knownEntities.has(entity)))
  }, [piiShieldData])
  const displayedEntities = isEditing ? draftEntities : savedEntities

  const isDirty = useMemo(() => draftEntities.symmetricDifference(originalEntities).size > 0, [draftEntities, originalEntities])

  const areAllSelected = draftEntities.size === ALL_PII_ENTITIES.length

  const handleToggleExpand = useCallback((categoryId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(categoryId)) {
        next.delete(categoryId)
      } else {
        next.add(categoryId)
      }
      return next
    })
  }, [])

  const handleEditClick = () => {
    setEditState({ isEditing: true, draftEntities: new Set(savedEntities), originalEntities: new Set(savedEntities), isConfirming: false })
    setExpandedIds(new Set(PII_SHIELD_CATEGORIES.map((category) => category.id)))
  }

  const handleToggleEntity = useCallback((entity: PiiEntity) => {
    setEditState((prev) => {
      const next = new Set(prev.draftEntities)
      if (next.has(entity)) {
        next.delete(entity)
      } else {
        next.add(entity)
      }
      return { ...prev, draftEntities: next }
    })
  }, [])

  const handleToggleCategory = useCallback((entities: PiiEntity[], allRedacted: boolean) => {
    setEditState((prev) => {
      const next = new Set(prev.draftEntities)
      entities.forEach((entity) => (allRedacted ? next.delete(entity) : next.add(entity)))
      return { ...prev, draftEntities: next }
    })
  }, [])

  const handleToggleAll = () => {
    setEditState((prev) => ({ ...prev, draftEntities: areAllSelected ? new Set() : new Set(ALL_PII_ENTITIES) }))
  }

  const handleDiscard = () => {
    setEditState(INITIAL_EDIT_STATE)
    setExpandedIds(new Set())
  }

  const handleSave = () => {
    setEditState((prev) => ({ ...prev, isConfirming: true }))
  }

  const handleBackFromConfirm = () => {
    setEditState((prev) => ({ ...prev, isConfirming: false }))
  }

  const handleConfirm = useCallback(async () => {
    try {
      await setPiiShieldConfig({ accountId, clientId, entities: Array.from(draftEntities) }).unwrap()
      dispatch(
        addToast({
          variant: 'success',
          title: 'Success',
          messages: ['Successfully updated.'],
        }),
      )
      setEditState(INITIAL_EDIT_STATE)
      setExpandedIds(new Set())
    } catch {
      dispatch(addToast({ variant: 'error', title: 'Error', messages: ['Failed to update PII redaction settings. Please try again.'] }))
    }
  }, [setPiiShieldConfig, accountId, clientId, draftEntities, dispatch])

  return (
    <Container fluid className='px-0'>
      <div className='flex-grow-1 zocks-pb-10.0 pii-shield-content'>
        <div className='d-flex align-items-center zocks-gap-2 mb-3'>
          <h4 className='mb-0'>PII Shield</h4>
        </div>

        <p className='paragraph-medium zocks-text-gray-900'>
          Control what {appName} sees when it connects to Zocks. Sensitive data is redacted before it ever leaves Zocks, so it never reaches the model.
        </p>
        {isActive ? (
          <p className='paragraph-medium zocks-text-gray-900'>
            These settings only apply to {appName}&apos;s MCP connection. To manage redaction for other apps, open each app&apos;s PII redaction tab.
            Workspace-level defaults are set in Setup &rarr; Data retention policies.
          </p>
        ) : null}

        {!quotaSuccess ? (
          <div className='d-flex align-items-center justify-content-center mt-5'>
            <Spinner />
          </div>
        ) : isActive ? (
          <>
            <div className='d-flex align-items-center justify-content-between mt-5 mb-2'>
              <h4 className='mb-0'>Categories</h4>
              {isEditing ? (
                <Button className='p-0 zocks-gap-0.5' onClick={handleToggleAll}>
                  {areAllSelected ? 'Deselect all' : 'Select all'}
                </Button>
              ) : (
                <Button className='p-0 zocks-gap-0.5' onClick={handleEditClick} disabled={isLoading || isError}>
                  <i className='bi bi-pencil-square' />
                  Edit
                </Button>
              )}
            </div>
            <div className='label-medium zocks-text-gray-600 mb-3'>{displayedEntities.size} Selected</div>

            {isLoading ? (
              <div className='d-flex align-items-center justify-content-center'>
                <Spinner />
              </div>
            ) : (
              PII_SHIELD_CATEGORIES.map((category) => (
                <PiiShieldCategoryCard
                  key={category.id}
                  category={category}
                  redactedEntities={displayedEntities}
                  isEditMode={isEditing}
                  isExpanded={expandedIds.has(category.id)}
                  onToggleExpand={handleToggleExpand}
                  onToggleEntity={handleToggleEntity}
                  onToggleCategory={handleToggleCategory}
                />
              ))
            )}
          </>
        ) : (
          <div className='zocks-mt-2.0'>
            <PiiShieldRequestAccess />
          </div>
        )}
      </div>

      <ConfirmSaveBar
        isVisible={isActive && isEditing}
        isConfirming={isConfirming}
        isSubmitting={isSaving}
        isDirty={isDirty}
        message='PII redaction changes will apply to all users and active connections in your account.'
        confirmMessage='This will update PII redaction categories for everyone in your account, including active connections.'
        onDiscard={handleDiscard}
        onSave={handleSave}
        onBack={handleBackFromConfirm}
        onConfirm={handleConfirm}
      />
    </Container>
  )
}
